import { Card, Grid, Icon, MenuItem, Radio, RadioGroup, FormControlLabel, Switch, CircularProgress, Paper, InputLabel, Dialog } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState, useRef } from "react";
import { get, post, postNoResponse } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { useUser } from "context/UserContext";
import CreateAgendaModal from "../../home/modal/CreateAgendaModal";

export default function CreateSolicitacao({ onGoBack, solicitacaoBase, solicitacaoPai, isCreatorView }) {
    const { userPerfils, userId, statusPrazoEnum, tipoEventoEnum } = useUser();
    const isEditing = !!solicitacaoBase;
    const [originalResponsavel] = useState(solicitacaoBase?.idUsuarioResponsavel || null);
    
    const [tipoGeracao, setTipoGeracao] = useState("SOLICITACAO"); // SOLICITACAO ou EVENTO
    const [usuariosAptos, setUsuariosAptos] = useState([]);
    const [membrosEquipe, setMembrosEquipe] = useState([]);
    const [equipesList, setEquipesList] = useState([]);
    
    const isGestorOuCoord = userPerfils?.some(p => p.nome?.toUpperCase().includes("GESTOR") || p.nome?.toUpperCase().includes("COORD") || p.nome?.toUpperCase().includes("SUPERV") || p.nome?.toUpperCase().includes("ADMIN"));
    
    // Buscas
    const debounceRef = useRef(null);
    const [tipoBusca, setTipoBusca] = useState("PROCESSO"); // PROCESSO ou CASO
    const [processosSearchList, setProcessosSearchList] = useState([]);
    const [loadingProcessos, setLoadingProcessos] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const [processoSearchInput, setProcessoSearchInput] = useState("");
    const [processoSelecionadoTexto, setProcessoSelecionadoTexto] = useState(solicitacaoBase?.idCasoProcessual ? "Processo Vinculado" : "");

    const [arvoreList, setArvoreList] = useState([]);
    const [loadingArvore, setLoadingArvore] = useState(false);

    const [subSolicitacaoModalOpen, setSubSolicitacaoModalOpen] = useState(false);
    const [subEventoModalOpen, setSubEventoModalOpen] = useState(false);

    const [form, setForm] = useState({
        id: solicitacaoBase?.id || null,
        titulo: solicitacaoBase?.titulo || "",
        descricao: solicitacaoBase?.descricao || "",
        dataPublicacao: "",
        tipoPrazo: null,
        statusPrazo: null,
        qtdDiasAvisar: 0,
        idEquipe: solicitacaoBase?.idEquipe || "",
        idUsuarioResponsavel: solicitacaoBase?.idUsuarioResponsavel || "",
        idCasoProcessual: solicitacaoBase?.idCasoProcessual || solicitacaoPai?.idCasoProcessual || null,
        idSolicitacaoPai: solicitacaoBase?.idSolicitacaoPai || solicitacaoPai?.id || null,
        prazo: solicitacaoBase?.prazo ? new Date(solicitacaoBase.prazo).toISOString().substring(0, 10) : "",
        urgente: solicitacaoBase?.urgente || false,
        status: solicitacaoBase?.status || 3
    });

    useEffect(() => {
        if (solicitacaoBase) {
            setForm({
                id: solicitacaoBase.id || null,
                titulo: solicitacaoBase.titulo || "",
                descricao: solicitacaoBase.descricao || "",
                idEquipe: solicitacaoBase.idEquipe || "",
                idUsuarioResponsavel: solicitacaoBase.idUsuarioResponsavel || "",
                idCasoProcessual: solicitacaoBase.idCasoProcessual || solicitacaoPai?.idCasoProcessual || null,
                idSolicitacaoPai: solicitacaoBase.idSolicitacaoPai || solicitacaoPai?.id || null,
                prazo: solicitacaoBase.prazo ? new Date(solicitacaoBase.prazo).toISOString().substring(0, 10) : "",
                urgente: solicitacaoBase.urgente || false,
                status: solicitacaoBase.status || 3
            });
        }
    }, [solicitacaoBase]);

    useEffect(() => {
        if (isGestorOuCoord) {
            get('/equipe/list-all').then(res => setEquipesList(res.list || [])).catch(() => setEquipesList([]));
        }
    }, [isGestorOuCoord]);

    useEffect(() => {
        if (form.idEquipe) {
            get(`/equipe/by-id/${form.idEquipe}`).then(res => {
                let list = [];
                if (res.usuarios) list = [...list, ...res.usuarios];
                if (res.usuariosCoordenadores) list = [...list, ...res.usuariosCoordenadores];
                
                // remove duplicates
                const unique = [];
                const map = new Map();
                for (const item of list) {
                    if (!map.has(item.id)) {
                        map.set(item.id, true);
                        unique.push(item);
                    }
                }
                setUsuariosAptos(unique);
            }).catch(() => setUsuariosAptos([]));
        } else {
            get('/users/list-all-coordinators').then(res => {
                const data = Array.isArray(res) ? res : (res.list || []);
                setUsuariosAptos(data);
            }).catch(() => setUsuariosAptos([]));
        }
    }, [form.idEquipe]);

    useEffect(() => {
        if (isSelecting || !processoSearchInput) {
            setProcessosSearchList([]);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setLoadingProcessos(true);
            setSearchError(null);
            
            if (tipoBusca === "PROCESSO") {
                get(`/processo/list-all?search=${encodeURIComponent(processoSearchInput.replace(/\D/g, ''))}`)
                    .then((res) => {
                        setProcessosSearchList(res.list || []);
                    })
                    .catch((err) => {
                        setProcessosSearchList([]);
                        setSearchError("Erro ao buscar processos");
                    })
                    .finally(() => setLoadingProcessos(false));
            } else {
                post("/processo/list-casos", { nomeCaso: processoSearchInput, pagination: { pageIndex: 0, pageSize: 15 }})
                    .then(({ data }) => setProcessosSearchList(data || []))
                    .catch((err) => {
                        setProcessosSearchList([]);
                        setSearchError("Erro ao buscar Casos");
                    })
                    .finally(() => setLoadingProcessos(false));
            }
        }, 500);

        return () => clearTimeout(debounceRef.current);
    }, [processoSearchInput, tipoBusca]);

    useEffect(() => {
        if (!userId) return;
        
        // Sub-Eventos (Agenda) só podem ser gerados para Membros da equipe do atual solicitante
        get(`/equipe/usuarios/${userId}`).then(res => {
            const data = Array.isArray(res) ? res : (res.list || []);
            if (data.length === 0) {
                // Trato alternativo caso equipe/usuarios nao traga por falha estrutural, busca todo mundo e filtra
                get("/users/list-all").then(res2 => {
                    let fallback = Array.isArray(res2) ? res2 : (res2.list || []);
                    setMembrosEquipe(fallback.filter(u => u.perfils?.some(p => p.nome.toLowerCase().includes("membro"))));
                }).catch(() => setMembrosEquipe([]));
            } else {
                setMembrosEquipe(data);
            }
        }).catch(() => setMembrosEquipe([]));
    }, [userId]);
    const carregarArvore = () => {
        if (isEditing && form.id) {
            setLoadingArvore(true);
            get(`/solicitacoes/arvore/${form.id}`)
                .then(res => {
                    const data = Array.isArray(res) ? res : [];
                    // Filtrar apenas descendentes diretos
                    setArvoreList(data.filter(item => item.idSolicitacaoPai === form.id));
                })
                .catch(err => console.error(err))
                .finally(() => setLoadingArvore(false));
        }
    };

    useEffect(() => {
        carregarArvore();
    }, [isEditing, form.id]);

    const handleSave = () => {
        let body = { ...form };
        
        if (isEditing && form.idUsuarioResponsavel !== originalResponsavel) {
            body.id = null; // Cria nova para o repasse
            body.idSolicitacaoPai = solicitacaoBase.id; 
            body.status = 3; // Volta para Pendente o repasse
        }

        // Envia para o Backend
        if (tipoGeracao === "SOLICITACAO") {
            postNoResponse("/solicitacoes/salvar", body)
                .then(() => onGoBack())
                .catch(err => errorAlert(err?.message ? `Erro ao salvar: ${err.message}` : "Erro ao salvar!"));
                } else {
            // Lógica de Salvar Evento na Agenda diretamente
            const eventoBody = {
                data: body.prazo,
                tipoPrazo: body.tipoPrazo,
                dataPublicacao: body.dataPublicacao,
                statusPrazo: body.statusPrazo, // <--- Isso corrige o erro de mandar o '3' estático
                pessoa: { id: body.idUsuarioResponsavel },
                qtdDiasAvisar: parseInt(body.qtdDiasAvisar) || 0,
                descricao: body.titulo + (body.descricao ? ' - ' + body.descricao : ''),
                processo: body.idCasoProcessual ? { id: body.idCasoProcessual } : null,
                tbSolicitacao: body.idSolicitacaoPai ? { id: body.idSolicitacaoPai } : null
            };
            postNoResponse("/prazo/save", eventoBody)
                .then(() => {
                    successAlert("Evento criado na Agenda com sucesso!");
                    onGoBack();
                })
                .catch((err) => errorAlert(err?.message ? `Erro ao criar Evento na Agenda: ${err.message}` : "Erro ao criar Evento na Agenda!"));
        }
    };

    return (
        <MDBox pt={3} pb={3}>
            <Card>
                <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ cursor: "pointer", fontWeight: "bold", fontSize: "28px !important", color: "inherit", mr: 1 }} onClick={onGoBack}>arrow_back</Icon>
                        <MDTypography variant="h5" fontWeight="bold" color="text">{isEditing ? "Editar Solicitação" : "Nova Solicitação"}</MDTypography>
                    </MDBox>
                    {isEditing && (
                        <MDBox display="flex" alignItems="center" gap={2}>
                            <MDTypography variant="h6" color="text">Status:</MDTypography>
                            <MDTypography variant="h6" color="info">
                                {form.status === 1 ? 'Concluída' : form.status === 2 ? 'Agendada' : 'Pendente'}
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
                <MDBox p={3}>
                    
                    <Grid container spacing={3}>
                        {!isEditing && !solicitacaoPai && (
                            <Grid item xs={12} sm={12}>
                                <MDTypography variant="button" fontWeight="bold">Tipo de Registro a ser Gerado:</MDTypography>
                                <MDBox display="flex" gap={2} mt={1}>
                                    <MDButton 
                                        variant={tipoGeracao === "SOLICITACAO" ? "gradient" : "outlined"} 
                                        color="info" 
                                        onClick={() => setTipoGeracao("SOLICITACAO")}
                                    >
                                        Gerar Solicitação
                                    </MDButton>
                                    <MDButton 
                                        variant={tipoGeracao === "EVENTO" ? "gradient" : "outlined"} 
                                        color="warning" 
                                        onClick={() => setTipoGeracao("EVENTO")}
                                    >
                                        Gerar Evento
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Título</InputLabel>
                            <MDInput 
                                fullWidth 
                                disabled={isEditing}
                                value={form.titulo} 
                                onChange={(e) => setForm({...form, titulo: e.target.value})} 
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Prazo Final</InputLabel>
                            <MDInput 
                                type={form.urgente ? "datetime-local" : "date"}
                                fullWidth 
                                disabled={isEditing}
                                value={form.prazo} 
                                onChange={(e) => setForm({...form, prazo: e.target.value})} 
                            />
                        </Grid>

                        <Grid item xs={12} sm={3} display="flex" alignItems="center">
                            <MDBox display="flex" alignItems="center">
                                <Switch checked={form.urgente} disabled={isEditing} onChange={(e) => setForm({...form, urgente: e.target.checked})} color="error" />
                                <MDTypography variant="button" fontWeight="regular" ml={1}>Urgente</MDTypography>
                            </MDBox>
                        </Grid>

                        {isGestorOuCoord && (
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Equipe</InputLabel>
                                <MDInput 
                                    select
                                    fullWidth 
                                    disabled={isEditing}
                                    value={form.idEquipe} 
                                    onChange={(e) => setForm({...form, idEquipe: e.target.value, idUsuarioResponsavel: ""})}
                                >
                                    <MenuItem value="">Nenhuma Equipe (Padrão)</MenuItem>
                                    {equipesList.map((e) => (
                                        <MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>
                                    ))}
                                </MDInput>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Responsável</InputLabel>
                            <MDInput 
                                select
                                fullWidth 
                                disabled={isEditing}
                                value={form.idUsuarioResponsavel} 
                                onChange={(e) => setForm({...form, idUsuarioResponsavel: e.target.value})}
                            >
                                <MenuItem value="" disabled>Selecione um Usuário Adequado</MenuItem>
                                {usuariosAptos.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>{u.nome}</MenuItem>
                                ))}
                            </MDInput>
                        </Grid>

                        {tipoGeracao === "EVENTO" && (
                            <>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data da Publicação</InputLabel>
                                    <MDInput type="date" fullWidth value={form.dataPublicacao} onChange={(e) => setForm({...form, dataPublicacao: e.target.value})} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Qtd dias antes para avisar</InputLabel>
                                    <MDInput type="number" fullWidth value={form.qtdDiasAvisar} onChange={(e) => setForm({...form, qtdDiasAvisar: e.target.value})} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo do Evento</InputLabel>
                                    <MDInput select fullWidth value={form.tipoPrazo} onChange={(e) => setForm({...form, tipoPrazo: e.target.value})}>
                                        <MenuItem value="" disabled>Selecione</MenuItem>
                                        {(tipoEventoEnum || []).map(o => <MenuItem key={o.code} value={o.code}>{o.name}</MenuItem>)}
                                    </MDInput>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status do Evento</InputLabel>
                                    <MDInput select fullWidth value={form.statusPrazo} onChange={(e) => setForm({...form, statusPrazo: e.target.value})}>
                                        <MenuItem value="" disabled>Selecione</MenuItem>
                                        {(statusPrazoEnum || []).map(o => <MenuItem key={o.code} value={o.code}>{o.name}</MenuItem>)}
                                    </MDInput>
                                </Grid>
                            </>
                        )}
                        {(isEditing && tipoGeracao === "SOLICITACAO") && (
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Marcar Status</InputLabel>
                                <MDInput 
                                    select
                                    fullWidth 
                                    value={form.status} 
                                    onChange={(e) => setForm({...form, status: e.target.value})}
                                >
                                    <MenuItem value={3}>Pendente</MenuItem>
                                    <MenuItem value={2}>Agendado</MenuItem>
                                    <MenuItem value={1}>Concluído</MenuItem>
                                </MDInput>
                            </Grid>
                        )}

                        {(!isEditing || form.idCasoProcessual) && (
                            <Grid item xs={12} sm={12}>
                                <MDTypography variant="button" fontWeight="bold">Vincular Processo (Opcional):</MDTypography>
                            <MDBox display="flex" alignItems="center" gap={2} mb={1}>
                                <RadioGroup
                                    row
                                    value={tipoBusca}
                                    onChange={(e) => {
                                        setTipoBusca(e.target.value);
                                        setProcessosSearchList([]);
                                        setProcessoSearchInput("");
                                    }}
                                >
                                    <FormControlLabel value="PROCESSO" control={<Radio disabled={isEditing} />} label="Processo Judicial" />
                                    <FormControlLabel value="CASO" control={<Radio disabled={isEditing} />} label="Caso Simplificado" />
                                </RadioGroup>
                            </MDBox>
                            
                            {processoSelecionadoTexto && (
                                <MDBox mb={2} p={1} sx={{ border: '1px solid #ccc', borderRadius: '4px', bgcolor: '#f5f5f5' }}>
                                    <MDTypography variant="button" fontWeight="bold" color="success">
                                        Vinculado: {processoSelecionadoTexto}
                                    </MDTypography>
                                    {!isEditing && (
                                        <MDButton size="small" color="error" variant="text" onClick={() => {
                                            setForm({...form, idCasoProcessual: null});
                                            setProcessoSelecionadoTexto("");
                                            setProcessoSearchInput("");
                                        }}>
                                            Remover
                                        </MDButton>
                                    )}
                                </MDBox>
                            )}

                            {!isEditing && !processoSelecionadoTexto && (
                                <div style={{ position: "relative" }}>
                                    <MDInput
                                        label={tipoBusca === "PROCESSO" ? "Pesquise por Número" : "Pesquise pelo Nome do Caso"}
                                        type="text"
                                        fullWidth

                                        value={processoSearchInput}
                                        onChange={(e) => setProcessoSearchInput(e.target.value)}
                                    />

                                    {(processosSearchList.length > 0 || loadingProcessos || searchError) && (
                                        <Paper elevation={3} style={{ position: "absolute", width: "100%", zIndex: 20, maxHeight: 250, overflowY: "auto" }}>
                                            {loadingProcessos && (
                                                <MenuItem><CircularProgress size={20} />&nbsp; Buscando...</MenuItem>
                                            )}

                                            {!loadingProcessos && searchError && (
                                                <MenuItem disabled sx={{ color: 'error.main' }}>⚠️ {searchError}</MenuItem>
                                            )}

                                            {!loadingProcessos && !searchError && processosSearchList.map((p) => {
                                                const labelStr = tipoBusca === "PROCESSO" ? `${p.processo} - ${p.cliente}` : p.nomeCaso;
                                                return (
                                                    <MenuItem
                                                        key={p.id}
                                                        onClick={() => {
                                                            setIsSelecting(true);
                                                            setForm({...form, idCasoProcessual: p.id});
                                                            setProcessoSelecionadoTexto(labelStr);
                                                            setProcessosSearchList([]);
                                                            setProcessoSearchInput("");
                                                            setLoadingProcessos(false);
                                                            setTimeout(() => setIsSelecting(false), 300);
                                                        }}
                                                    >
                                                        <b>{tipoBusca === "PROCESSO" ? p.processo : p.nomeCaso}</b>
                                                        {tipoBusca === "PROCESSO" && ` - ${p.cliente}`}
                                                    </MenuItem>
                                                )
                                            })}
                                        </Paper>
                                    )}
                                </div>
                            )}
                        </Grid>
                        )}

                        <Grid item xs={12} sm={12}>
                            <MDInput 
                                fullWidth 
                                multiline 
                                disabled={isEditing}
                                rows={4} 
                                label="Descrição do Pedido / Tarefa"
                                InputLabelProps={{ shrink: true }}


                                value={form.descricao} 
                                onChange={(e) => setForm({...form, descricao: e.target.value})} 
                            />
                        </Grid>
                        
                        {isEditing && (
                            <Grid item xs={12} sm={12}>
                                <MDBox mt={3} p={2} sx={{ border: '1px dashed #ccc', borderRadius: '8px', bgcolor: '#fafafa' }}>
                                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                                        Desdobramentos (Sub-tarefas e Eventos)
                                    </MDTypography>
                                    
                                    {loadingArvore ? (
                                        <CircularProgress size={20} />
                                    ) : arvoreList.length === 0 ? (
                                        <MDTypography variant="caption" color="text">
                                            Nenhuma sub-tarefa criada ainda.
                                        </MDTypography>
                                    ) : (
                                        <MDBox display="flex" flexDirection="column" gap={1}>
                                            {arvoreList.map((item, index) => (
                                                <Paper key={item.id || index} sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                                                    <MDTypography variant="button" fontWeight="bold">
                                                        {item.titulo}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="text">
                                                        {item.descricao || "Sem descrição"}
                                                    </MDTypography>
                                                </Paper>
                                            ))}
                                        </MDBox>
                                    )}
                                    
                                    {!isCreatorView && (
                                        <MDBox display="flex" gap={2} mt={2}>
                                            <MDButton 
                                                variant="outlined" 
                                                color="info" 
                                                onClick={() => setSubSolicitacaoModalOpen(true)}
                                            >
                                                + Adicionar Solicitação
                                            </MDButton>
                                            <MDButton 
                                                variant="outlined" 
                                                color="warning" 
                                                onClick={() => setSubEventoModalOpen(true)}
                                            >
                                                + Adicionar Evento
                                            </MDButton>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </Grid>
                        )}

                        <Grid item xs={12} mt={2}>
                            <MDButton variant="gradient" color="info" onClick={handleSave}>
                                SALVAR {tipoGeracao === "SOLICITACAO" ? "SOLICITAÇÃO" : "EVENTO"}
                            </MDButton>
                        </Grid>
                    </Grid>
                </MDBox>
            </Card>

            <Dialog open={subSolicitacaoModalOpen} onClose={() => setSubSolicitacaoModalOpen(false)} maxWidth="md" fullWidth>
                <CreateSolicitacao 
                    solicitacaoPai={form}
                    onGoBack={() => {
                        setSubSolicitacaoModalOpen(false);
                        carregarArvore();
                    }}
                />
            </Dialog>

            {subEventoModalOpen && (
                <CreateAgendaModal 
                    open={subEventoModalOpen}
                    onClose={() => setSubEventoModalOpen(false)}
                    usuarios={membrosEquipe}
                    fetchAgenda={() => {
                        setSubEventoModalOpen(false);
                        carregarArvore();
                    }}
                    eventoPai={{ idSolicitacaoPai: form.id, idCasoProcessual: form.idCasoProcessual }}
                />
            )}
        </MDBox>
    );
}
